import { useEffect, useMemo, useState } from "react";
import { Loader2, Sparkles } from "lucide-react";
import { api, type Model } from "@/api/client";
import type { RollupPeriodType } from "../../../types";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { NativeSelect } from "@/components/ui/native-select";
import { recentPeriods } from "./periodOptions";

const PERIOD_TYPES: { value: RollupPeriodType; label: string }[] = [
  { value: "month", label: "Monthly" },
  { value: "quarter", label: "Quarterly" },
  { value: "year", label: "Yearly" },
];

interface CreateRollupFormProps {
  onSuccess: (rollupId: number) => void;
  onCancel: () => void;
}

export function CreateRollupForm({ onSuccess, onCancel }: CreateRollupFormProps) {
  const [periodType, setPeriodType] = useState<RollupPeriodType>("month");
  const [periodKey, setPeriodKey] = useState("");
  const [model, setModel] = useState(""); // "" = all models
  const [models, setModels] = useState<Model[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const periods = useMemo(() => recentPeriods(periodType), [periodType]);

  // Default to the most recent complete period (index 1) so we don't preselect a
  // period that has barely started; fall back to the latest if only one exists.
  useEffect(() => {
    setPeriodKey(periods[1]?.key ?? periods[0]?.key ?? "");
  }, [periods]);

  useEffect(() => {
    api.models.list().then(setModels).catch(() => {});
  }, []);

  const handleSubmit = async () => {
    if (!periodKey) return;
    setSubmitting(true);
    setError("");
    try {
      const rollup = await api.rollups.create({
        period_type: periodType,
        period_key: periodKey,
        model: model || undefined,
      });
      onSuccess(rollup.id);
    } catch (err: any) {
      setError(err?.message || "Failed to generate rollup");
      setSubmitting(false);
    }
  };

  return (
    <Card>
      <CardContent className="py-5 px-6 space-y-4">
        <div className="grid gap-4 sm:grid-cols-3">
          <div>
            <Label htmlFor="rollup-type">Period</Label>
            <NativeSelect id="rollup-type" value={periodType} onChange={e => setPeriodType(e.target.value as RollupPeriodType)}>
              {PERIOD_TYPES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
            </NativeSelect>
          </div>
          <div>
            <Label htmlFor="rollup-period">Covering</Label>
            <NativeSelect id="rollup-period" value={periodKey} onChange={e => setPeriodKey(e.target.value)}>
              {periods.map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
            </NativeSelect>
          </div>
          <div>
            <Label htmlFor="rollup-model">Model</Label>
            <NativeSelect id="rollup-model" value={model} onChange={e => setModel(e.target.value)}>
              <option value="">All models</option>
              {models.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
            </NativeSelect>
          </div>
        </div>

        <p className="text-[12px] text-slate-400 leading-relaxed">
          Summarizes recorded visibility &amp; sentiment for the selected period from your tracking history,
          with an AI-written recap. No new model runs are made.
        </p>

        {error && <p className="text-[12px] text-red-600">{error}</p>}

        <div className="flex gap-2">
          <Button onClick={handleSubmit} disabled={submitting || !periodKey}>
            {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
            Generate Rollup
          </Button>
          <Button variant="ghost" onClick={onCancel} disabled={submitting}>Cancel</Button>
        </div>
      </CardContent>
    </Card>
  );
}
